import type { Theme } from "@mui/material";

/**
 * Stable, distinct colours for comparison groups (Region A vs B vs …).
 * Indexed by group order so a group keeps its colour across every chart on the page.
 */
export const getComparisonGroupColor = (index: number, theme: Theme): string => {
  const palette = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
  ];
  return palette[index % palette.length];
};

export interface ComparisonTrendSeriesInput {
  /** Group label, becomes the series label (e.g. "EMEA"). */
  label: string;
  /** Group colour. */
  color: string;
  /** Per-group transformed trend rows keyed by period label. */
  data: Array<Record<string, string | number | null | undefined>>;
}

export interface MergedTrendResult {
  /** Rows keyed by x-axis label, each carrying one value per group series. */
  data: Array<Record<string, string | number | null>>;
  /** Series descriptors (one per group) for the LineChart. */
  series: Array<{ dataKey: string; label: string; color: string; area: boolean }>;
}

/**
 * Merge N per-group trend datasets into a single dataset + series list so a
 * LineChart can overlay one line per group for the chosen focus metric.
 *
 * Each group's value for `focusMetric` is re-keyed to a unique series dataKey
 * (`group_<i>`), and rows are merged on the shared x-axis label.
 */
export const buildComparisonTrendSeries = (
  groups: ComparisonTrendSeriesInput[],
  focusMetric: string,
  xAxisKey = "label"
): MergedTrendResult => {
  const rowsByX = new Map<string, Record<string, string | number | null>>();
  const series: MergedTrendResult["series"] = [];

  groups.forEach((group, index) => {
    const dataKey = `group_${index}`;
    series.push({ dataKey, label: group.label, color: group.color, area: false });

    group.data.forEach((row) => {
      const xValue = row[xAxisKey];
      if (xValue == null) return;
      const xKey = String(xValue);
      const existing = rowsByX.get(xKey) ?? { [xAxisKey]: xKey };
      const value = row[focusMetric];
      existing[dataKey] = typeof value === "number" ? value : null;
      rowsByX.set(xKey, existing);
    });
  });

  return { data: Array.from(rowsByX.values()), series };
};

export interface ComparisonPatternSeriesInput {
  /** Group label, becomes the series label. */
  label: string;
  /** Group colour. */
  color: string;
  /** Per-group pattern points (e.g. { time, value }). */
  data: Array<{ time?: string; day?: string; value: number | null }>;
}

/**
 * Merge N per-group pattern datasets into a single dataset + series list so a
 * LineChart can overlay one line per group across the shared x-axis (time of
 * day or day of week). Each group's `value` is re-keyed to `group_<i>`.
 */
export const buildComparisonPatternSeries = (
  groups: ComparisonPatternSeriesInput[],
  xAxisKey: "time" | "day" = "time"
): MergedTrendResult => {
  const rowsByX = new Map<string, Record<string, string | number | null>>();
  const series: MergedTrendResult["series"] = [];

  groups.forEach((group, index) => {
    const dataKey = `group_${index}`;
    series.push({ dataKey, label: group.label, color: group.color, area: false });

    group.data.forEach((point) => {
      const xValue = point[xAxisKey];
      if (xValue == null) return;
      const xKey = String(xValue);
      const existing = rowsByX.get(xKey) ?? { [xAxisKey]: xKey };
      existing[dataKey] = typeof point.value === "number" ? point.value : null;
      rowsByX.set(xKey, existing);
    });
  });

  return { data: Array.from(rowsByX.values()), series };
};
